import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

/**
 * VerifyEmail — completes the email-verification flow.
 *
 * The user lands here from the emailed magic link (/verify-email?token=…,
 * built in backend/routers/auth.py::send_verification). Calls
 * GET /api/auth/verify-email?token=… once on mount; that single request
 * both validates the token and flips users.email_verified server-side.
 * Mirrors ResetPassword.jsx's layout/shape for the equivalent flow.
 */
export default function VerifyEmail() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [status, setStatus] = useState(token ? "loading" : "missing"); // loading | done | error | missing
    const [err, setErr] = useState("");
    const ran = useRef(false);

    useEffect(() => {
        if (!token || ran.current) return;
        ran.current = true;
        (async () => {
            try {
                const base = process.env.REACT_APP_BACKEND_URL || "";
                const r = await fetch(`${base}/api/auth/verify-email?token=${encodeURIComponent(token)}`, {
                    credentials: "include",
                });
                const data = await r.json().catch(() => ({}));
                if (!r.ok) {
                    setErr(formatApiErrorDetail(data.detail) || "This verification link is invalid or has expired.");
                    setStatus("error");
                    return;
                }
                setStatus("done");
            } catch {
                setErr("Network error. Please try again.");
                setStatus("error");
            }
        })();
    }, [token]);

    return (
        <div
            className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#03122b] px-6 text-white"
            data-testid="verify-email-page"
        >
            <div className="pointer-events-none absolute inset-0 grid-bg opacity-[0.12]" />
            <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-[#00C2FF]/12 blur-[160px]" />
            <div className="pointer-events-none absolute -right-40 bottom-1/4 h-[420px] w-[420px] rounded-full bg-[#5b5ef0]/14 blur-[160px]" />

            <div className="relative z-10 w-full max-w-md">
                <div className="mb-8 flex items-center justify-center">
                    <img
                        src="/brand/moovbytes-logo-white.png"
                        alt="MoovBytes"
                        className="h-8 w-auto select-none"
                    />
                </div>

                <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-8">
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00C2FF]/60 to-transparent" />

                    {status === "missing" ? (
                        <div data-testid="verify-missing-token">
                            <h1 className="font-display text-2xl font-bold tracking-tight">
                                Missing verification token
                            </h1>
                            <p className="mt-2 text-sm text-white/65">
                                This page needs a one-time link from your verification email.
                                Sign in and request a new one from your Portal.
                            </p>
                        </div>
                    ) : status === "loading" ? (
                        <div data-testid="verify-loading" className="flex flex-col items-center py-4 text-center">
                            <Loader2 size={32} className="animate-spin text-[#00C2FF]" />
                            <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">
                                Verifying your email…
                            </h1>
                        </div>
                    ) : status === "done" ? (
                        <div data-testid="verify-success">
                            <CheckCircle2 size={32} className="text-[#10b981]" />
                            <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">
                                Email verified
                            </h1>
                            <p className="mt-2 text-sm text-white/65">
                                You're all set — device activation and team invites are unlocked.
                            </p>
                        </div>
                    ) : (
                        <div data-testid="verify-error">
                            <XCircle size={32} className="text-[#ef4444]" />
                            <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">
                                Couldn't verify your email
                            </h1>
                            <p className="mt-2 text-sm text-white/65">{err}</p>
                            <p className="mt-2 text-sm text-white/65">
                                Sign in and use "Send verification email" again for a fresh link.
                            </p>
                        </div>
                    )}

                    <Link
                        to="/login"
                        data-testid="verify-back-link"
                        className="mt-6 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 transition hover:text-white"
                    >
                        <ArrowLeft size={12} /> Back to log in
                    </Link>
                </div>
            </div>
        </div>
    );
}
